import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  Target,
  Rocket,
  Phone,
  FileText,
  Compass,
  Award,
} from 'lucide-react';
import SEO from '../components/SEO';
import {
  BUNDLE_PRICE,
  BUNDLE_PRICE_STRIKE,
  BUNDLE_SAVINGS,
  BUNDLE_CHECKOUT_URL,
  BUNDLE_CHECKOUT_READY,
  MENTORSHIP_CHECKOUT_URL,
  PAYMENT_OPTIONS_SHORT,
} from '../constants/site';

const DISCOVERY_CALL_URL = 'https://calendly.com/nicolas-gate33/discovery-call';

// ---- Photography (private aviation, warm cabin / hangar style) ----
const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/jk30h5vz_Global-Express-Charter-Interior-Front-View-scaled.jpg';
const AUDIENCE_IMG =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/pd3x5f8l_global7500_173338570_d14162cfb8_332857f3fd.webp';
// Image for the new "Why this mentorship exists" section — reuses an existing site image
// (already used on the About / catering shoot), so nothing new is created.
const WHY_MENTORSHIP_IMG =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/k0a6b12n_bram-kahmann-exxaero-shoot-food_-21.jpg';

const FULL_MONTHS = [
  {
    n: '01',
    title: 'Positioning & CV',
    body: 'Full CV/LinkedIn rebuild, personal narrative, and positioning strategy tailored to the operators that fit your profile.',
  },
  {
    n: '02',
    title: 'Applications & Strategy',
    body: 'Access to our operator list, updated as openings come up, an application plan and outreach templates. Direct introductions to operators whenever we can.',
  },
  {
    n: '03',
    title: 'Acceleration & Industry Insight',
    body: '2 mock interviews, ongoing application adjustments, priority notice on new operator openings. Convert opportunities into offers.',
  },
];

const FULL_HIGHLIGHTS = [
  'Profile, CV & LinkedIn rebuild',
  'Access to our operator list',
  'Direct introductions whenever possible',
  '6× 1:1 coaching calls (once every two weeks)',
  'WhatsApp access for the full 3 months',
  'Mock interviews & priority notice on new openings',
];

const AUDIENCES = [
  {
    id: 'graduates',
    icon: Award,
    label: 'Gate 33 Graduates',
    body:
      'You completed the 3-Day Online Training and want continued 1:1 support as you turn your new positioning into interviews and offers.',
  },
  {
    id: 'experienced',
    icon: Compass,
    label: 'Experienced Candidates',
    body:
      "You come from an airline, a luxury hotel, fine dining, a private household or a yacht, and you're struggling to land the right private-jet role. Mentorship reshapes your profile, gives you access to the right operators and a direct channel to Nicolas.",
  },
];

const HOW_STEPS = [
  {
    n: '01',
    icon: FileText,
    title: 'Your profile & CV, reshaped',
    body: 'We rebuild your CV, LinkedIn and personal story in the language operators look for, so your airline or hospitality background reads as a private aviation profile.',
  },
  {
    n: '02',
    icon: Target,
    title: 'Access to our operator list',
    body: 'You get our curated list of operators that fit your profile, updated as new openings come up. You apply with a clear plan, not at random.',
  },
  {
    n: '03',
    icon: Phone,
    title: 'Direct introductions',
    body: 'Whenever we can, we connect you directly with operators and recruiters in our network.',
  },
  {
    n: '04',
    icon: MessageSquare,
    title: '1:1 coaching',
    body: 'Regular 1:1 calls plus WhatsApp between sessions. Every application, every follow-up and every interview prepared with you, including mock interviews.',
  },
];

const TESTIMONIAL = {
  quote:
    'What stood out immediately was his understanding of what private aviation operators are actually looking for beyond a standard airline profile.',
  name: 'Matteo Chivilo',
  role: 'Cabin Crew — Business Aviation',
  photo: '/recommendations/matteo.png',
};

const MentorshipPage = () => (
  <div data-testid="mentorship-page" className="min-h-screen">
    <SEO
      title="3-Month Mentorship — €2,200"
      description="Three months of structured 1:1 guidance to reposition your profile, apply to the right operators, and prepare for the interviews that matter."
      path="/mentorship"
    />
    {/* ===== HERO (photo header) ===== */}
    <section
      data-testid="mentorship-hero"
      className="relative min-h-[80vh] flex items-center"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Gate 33 Mentorship — private aviation cabin interior"
          title="Gate 33 Mentorship — 1:1 support to enter private aviation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/85 via-[#0F0F0F]/60 to-[#0F0F0F]/20" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.22em] font-medium text-[#C5A880] inline-block mb-6">
            Mentorship
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Get positioned. Get hired by the right operator.
          </h1>
          <p className="text-lg text-[#E5E0D8] leading-relaxed mb-4">
            Mentorship is 1:1 coaching to get you hired in private aviation. We reshape your profile and CV, give you access to our list of operators, and connect you with them directly whenever we can.
          </p>
          <p className="text-base text-[#C5A880] font-medium mb-10">
            Profile &amp; CV · Operator list · Direct introductions · 1:1 coaching
          </p>
          <a
            href={DISCOVERY_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mentorship-hero-cta"
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Book a Free Discovery Call <ArrowRight size={16} />
          </a>
          <p className="text-sm text-[#9A9A9A] mt-3">
            Free 45-minute call · no obligation.
          </p>
        </div>
      </div>
    </section>

    {/* ===== WHY THIS MENTORSHIP EXISTS — image LEFT · text RIGHT (alternates with next section) ===== */}
    <section data-testid="mentorship-why" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[4/5] image-hover">
              <img
                src={WHY_MENTORSHIP_IMG}
                alt="One-to-one mentoring — Gate 33"
                title="One-to-one mentoring that gets you hired"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -right-8 w-32 h-32 border border-[#C5A880] hidden lg:block" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="overline inline-block mb-4">Why this mentorship exists</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
              The one-to-one help that got me hired.
            </h2>
            <div className="space-y-5 text-[#5A5A5A] leading-relaxed">
              <p>
                Coming from Qatar Airways and Etihad, I sent my airline CV to around 120 private operators. Almost no one answered. I even paid for a VIP flight attendant training, and it only repeated what I already knew.
              </p>
              <p>
                What finally worked was one-to-one. A working VIP flight attendant sat down with me, rebuilt my CV and showed me how operators actually read a profile. Operators started viewing it straight away, and two of them hired me.
              </p>
              <p>
                This mentorship gives you that, and more: I reshape your profile and CV, open my list of operators to you, connect you with them whenever I can, and coach you through every application and interview for three months. Whether you come from an airline, a hotel, a restaurant or a private household.
              </p>
            </div>
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mentorship-why-discovery-link"
              onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'mentorship_why' })}
              className="inline-flex items-center gap-1.5 mt-8 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:text-[#0F0F0F] transition-colors"
            >
              Start with a free discovery call <ArrowRight size={12} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ===== WHO IT'S FOR (Two Audiences) ===== */}
    <section data-testid="mentorship-audiences" className="section-padding bg-white">
      <div className="container-custom max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="overline inline-block mb-4">Who It's For</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
              Two starting points. One goal — get hired.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed mb-8">
              Mentorship isn't reserved for Gate 33 graduates. It's built for anyone who
              needs sustained, honest guidance to enter private aviation — no matter where
              their training or experience started.
            </p>
            <ul className="space-y-6">
              {AUDIENCES.map((a) => (
                <li
                  key={a.id}
                  data-testid={`mentorship-audience-${a.id}`}
                  className="flex items-start gap-4"
                >
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-[#C5A880]">
                    <a.icon size={18} className="text-[#C5A880]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#1A1A1A] font-semibold mb-1">
                      {a.label}
                    </div>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed">{a.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] image-hover">
              <img
                src={AUDIENCE_IMG}
                alt="Gate 33 Mentorship — cabin crew and hospitality professionals entering private aviation"
                title="Gate 33 Mentorship — for graduates and experienced candidates"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 border border-[#C5A880] hidden lg:block" />
          </div>
        </div>
      </div>
    </section>

    {/* ===== HOW IT WORKS ===== */}
    <section data-testid="mentorship-how" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="overline inline-block mb-4">What you get</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
            Four things, for three months.
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed">
            No vague coaching. Everything is tied to one goal: getting you hired by the right operator.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {HOW_STEPS.map((step) => (
            <div
              key={step.n}
              data-testid={`mentorship-how-step-${step.n}`}
              className="grid grid-cols-[auto_1fr] gap-6 p-6 bg-white border border-[#E5E0D8]"
            >
              <div className="flex flex-col items-center">
                <div className="w-11 h-11 flex items-center justify-center border border-[#C5A880] mb-2">
                  <step.icon size={18} className="text-[#C5A880]" />
                </div>
                <span className="font-heading text-xs text-[#C5A880] tracking-widest">
                  {step.n}
                </span>
              </div>
              <div>
                <h3 className="font-heading text-lg mb-2 text-[#0F0F0F]">
                  {step.title}
                </h3>
                <p className="text-sm text-[#5A5A5A] leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ===== SINGLE OFFER · 3-Month Mentorship €2,200 ===== */}
    <section data-testid="mentorship-tiers" className="section-padding bg-white">
      <div className="container-custom max-w-4xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="overline inline-block mb-4">The Program</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
            3-Month Mentorship — €2,200
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed">
            Three months of structured 1:1 guidance to reposition your profile, apply to the right operators,
            and prepare for the interviews that matter. No group calls, no generic material —
            everything is tailored to your profile and target operators.
          </p>
        </div>

        <div
          data-testid="mentorship-tier-full"
          className="bg-[#0F0F0F] text-[#FDFBF7] p-10 md:p-14 relative"
        >
          <div className="flex items-center gap-3 mb-6">
            <Rocket size={22} className="text-[#C5A880]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#C5A880]">
              3-Month Mentorship
            </span>
          </div>

          {/* Top row — price/description LEFT · compact bundle upsell RIGHT */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-10 mb-12 items-stretch">
            {/* LEFT — price + description */}
            <div>
              <div className="flex flex-wrap items-baseline gap-4 mb-6">
                <span className="font-heading text-5xl text-[#FDFBF7] leading-none">€2,200</span>
                <span className="text-sm text-[#9A9A9A]">One-time · or up to 24× with PayPal · 3-month structured program</span>
              </div>
              <p className="text-[#9A9A9A] leading-relaxed max-w-xl">
                A structured, month-by-month program: from positioning, to active
                applications, to conversion — the goal is placement.
              </p>
            </div>

            {/* RIGHT — compact bundle upsell */}
            <div
              data-testid="mentorship-bundle-card"
              className="border border-[#C5A880]/40 bg-[#0F0F0F] p-5 md:p-6 relative flex flex-col justify-between"
            >
              <div className="absolute -top-px -right-px bg-[#C5A880] text-[#0F0F0F] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold">
                Save {BUNDLE_SAVINGS}
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-2">
                  Bundle · Complete path
                </span>
                <h3 className="font-heading text-lg sm:text-xl tracking-tight leading-tight text-[#FDFBF7] mb-3">
                  Add the 3-Day Training
                </h3>
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <span data-testid="mentorship-bundle-price" className="font-heading text-2xl text-[#FDFBF7] leading-none">
                    {BUNDLE_PRICE}
                  </span>
                  <span className="text-xs text-[#9A9A9A] line-through">{BUNDLE_PRICE_STRIKE}</span>
                </div>
                <p className="text-[11px] text-[#9A9A9A] mb-4">Training + Mentorship · up to 24× PayPal</p>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href={BUNDLE_CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mentorship-bundle-buy-cta"
                  onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'mentorship_bundle' })}
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-4 py-2.5 uppercase tracking-[0.15em] text-[11px] font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
                >
                  Buy the bundle <ArrowRight size={12} />
                </a>
                <Link
                  to="/academy"
                  data-testid="mentorship-bundle-training-link"
                  className="inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold hover:text-[#FDFBF7] transition-colors py-1"
                >
                  See training details <ArrowRight size={10} />
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {FULL_MONTHS.map((m) => (
              <div
                key={m.n}
                data-testid={`mentorship-full-month-${m.n}`}
                className="border-l-2 border-[#C5A880] pl-5"
              >
                <div className="mb-2">
                  <span className="font-heading text-lg text-[#C5A880]">
                    Month {m.n}
                  </span>
                  <div className="text-sm text-[#FDFBF7] font-medium mt-1">{m.title}</div>
                </div>
                <p className="text-sm text-[#9A9A9A] leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A880] mb-4">
            What's included
          </div>
          <ul className="space-y-3 mb-10 grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
            {FULL_HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-3">
                <CheckCircle2
                  size={16}
                  className="text-[#C5A880] flex-shrink-0 mt-1"
                />
                <span className="text-sm text-[#FDFBF7] leading-relaxed">{h}</span>
              </li>
            ))}
          </ul>

          <div className="pt-6 border-t border-[#C5A880]/30 flex flex-wrap gap-4">
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mentorship-full-cta"
              onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'mentorship_program' })}
              className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
            >
              Book a Free Discovery Call <ArrowRight size={14} />
            </a>
            <a
              href={MENTORSHIP_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mentorship-full-buy-cta"
              onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'mentorship_program' })}
              className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              Enrol — €2,200 <ArrowRight size={14} />
            </a>
            <p className="text-xs text-[#9A9A9A] mt-4 w-full">
              Most candidates start with a free 45-minute call. Already sure? Enrol directly. Pay in full or up to 24× with PayPal.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ===== TESTIMONIAL ===== */}
    <section
      data-testid="mentorship-testimonial"
      className="section-padding bg-[#FDFBF7]"
    >
      <div className="container-custom max-w-4xl text-center">
        <span className="overline inline-block mb-6">Industry Perspective</span>
        <blockquote
          data-testid="mentorship-testimonial-quote"
          className="font-heading text-2xl sm:text-3xl leading-relaxed text-[#0F0F0F] mb-10"
        >
          "{TESTIMONIAL.quote}"
        </blockquote>
        <div className="flex items-center justify-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden bg-[#E5E0D8]">
            <img
              src={TESTIMONIAL.photo}
              alt={TESTIMONIAL.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <div className="text-left">
            <div className="font-heading text-lg text-[#0F0F0F]">{TESTIMONIAL.name}</div>
            <div className="text-sm text-[#5A5A5A]">{TESTIMONIAL.role}</div>
          </div>
        </div>
      </div>
    </section>

    {/* ===== FINAL CTA BAND (plain black) ===== */}
    <section
      data-testid="mentorship-final-cta"
      className="section-dark py-24 md:py-28 border-b border-[#C5A880]/40"
    >
      <div className="container-custom text-center">
        <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
          Ready to move forward?
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
          Let's talk on a free discovery call.
        </h2>
        <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
          A free 45-minute call to understand your profile and confirm mentorship is the right fit.
        </p>
        <a
          href={DISCOVERY_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mentorship-final-cta-btn"
          className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
        >
          Book a Free 45-Minute Discovery Call <ArrowRight size={16} />
        </a>
      </div>
    </section>
  </div>
);

export default MentorshipPage;
